/**
 * DamageCheckMap 캘리브레이션 모드 — 개발자 전용 (`?calibrate=1`)
 *
 * 분리 사유: 본체 540줄 → 500 룰 위반. 캘리브레이션은 운영 미사용 영역.
 * 본체 유지: DAMAGE_TYPES / PARTS / OFFICIAL_BG / VIEWBOX / READONLY_VIEWBOX / renderCarSvg / 4 탭
 *           (회귀 테스트 src grep 가드 정합)
 *
 * 분리 영역:
 *   - getCalibrateMode() — URL ?calibrate=1 토글 감지
 *   - useCalibrateDrag() — 드래그/리사이즈 시작 + 글로벌 mousemove/mouseup hook
 *   - DamageCheckMapCalibrate — PARTS 좌표 출력 + 클립보드 복사 패널
 */
import React from 'react';

// URL ?calibrate=1 → 부위 좌표 캘리브레이션 모드 (드래그/리사이즈)
export function getCalibrateMode() {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('calibrate') === '1';
}

/**
 * 캘리브레이션 드래그/리사이즈 hook.
 *
 * @param {Object} viewbox - VIEWBOX map (view → {w, h})
 * @param {Object} partsState - 현재 PARTS 상태 (view → array)
 * @param {Function} setPartsState - PARTS 상태 setter
 * @param {Object} dragInfo - 드래그 진행 상태 (null = idle)
 * @param {Function} setDragInfo - 드래그 상태 setter
 * @param {boolean} calibrateMode - true 일 때만 활성
 * @returns {Function} startDrag(e, view, partIdx, mode) — onMouseDown 핸들러
 */
export function useCalibrateDrag(viewbox, partsState, setPartsState, dragInfo, setDragInfo, calibrateMode) {
  var startDrag = function(e, view, partIdx, mode) {
    if (!calibrateMode) return;
    e.preventDefault(); e.stopPropagation();
    var svg = e.currentTarget.ownerSVGElement;
    var rect = svg.getBoundingClientRect();
    var dim = viewbox[view];
    var sx = dim.w / rect.width;
    var sy = dim.h / rect.height;
    setDragInfo({ view: view, partIdx: partIdx, mode: mode, startX: e.clientX, startY: e.clientY, sx: sx, sy: sy, orig: Object.assign({}, partsState[view][partIdx]) });
  };
  React.useEffect(function() {
    if (!dragInfo) return;
    var onMove = function(e) {
      var dx = (e.clientX - dragInfo.startX) * dragInfo.sx;
      var dy = (e.clientY - dragInfo.startY) * dragInfo.sy;
      setPartsState(function(prev) {
        var next = Object.assign({}, prev);
        next[dragInfo.view] = prev[dragInfo.view].slice();
        var p = Object.assign({}, dragInfo.orig);
        if (dragInfo.mode === 'move') { p.x = Math.round(dragInfo.orig.x + dx); p.y = Math.round(dragInfo.orig.y + dy); }
        else if (dragInfo.mode === 'se') { p.w = Math.max(8, Math.round(dragInfo.orig.w + dx)); p.h = Math.max(8, Math.round(dragInfo.orig.h + dy)); }
        else if (dragInfo.mode === 'nw') { p.x = Math.round(dragInfo.orig.x + dx); p.y = Math.round(dragInfo.orig.y + dy); p.w = Math.max(8, Math.round(dragInfo.orig.w - dx)); p.h = Math.max(8, Math.round(dragInfo.orig.h - dy)); }
        next[dragInfo.view][dragInfo.partIdx] = p;
        return next;
      });
    };
    var onUp = function() { setDragInfo(null); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return function() { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragInfo]);
  return startDrag;
}

/**
 * PARTS 좌표 출력 + 클립보드 복사 패널 (캘리브레이션 모드에서 노출).
 */
export default function DamageCheckMapCalibrate(props) {
  var partsState = props.partsState;
  var formatParts = function(arr, name) {
    var lines = arr.map(function(p) {
      var extra = p.pillarKind ? ', pillarKind: \'' + p.pillarKind + '\'' : '';
      return '  { key: \'' + p.key + '\', label: \'' + p.label + '\', officialNo: ' + p.officialNo + ', rank: \'' + p.rank + '\', x: ' + p.x + ', y: ' + p.y + ', w: ' + p.w + ', h: ' + p.h + extra + ' },';
    }).join('\n');
    return 'var ' + name + ' = [\n' + lines + '\n];';
  };
  var output = [
    formatParts(partsState.top, 'PARTS_TOP'),
    formatParts(partsState.left, 'PARTS_LEFT'),
    formatParts(partsState.right, 'PARTS_RIGHT'),
    formatParts(partsState.bottom, 'PARTS_BOTTOM'),
  ].join('\n\n');
  var copyToClipboard = function() {
    navigator.clipboard.writeText(output).then(function() {
      alert('PARTS 코드 클립보드 복사 완료!');
    });
  };
  return (
    <div style={{ background: '#FEF2F2', border: '2px solid #E24B4A', borderRadius: 10, padding: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#E24B4A' }}>🔧 캘리브레이션 모드 — 박스 드래그/리사이즈로 좌표 조정</div>
        <button onClick={copyToClipboard} style={{
          padding: '6px 14px', borderRadius: 6, border: 'none', background: '#E24B4A',
          color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}>📋 PARTS 코드 복사</button>
      </div>
      <textarea value={output} readOnly style={{
        width: '100%', height: 200, fontSize: 10, fontFamily: 'monospace',
        border: '1px solid #ddd', borderRadius: 4, padding: 6, resize: 'vertical',
      }} />
    </div>
  );
}
